import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { DataTable, type Column } from '../components/data-table';
import { PageHeader, Row, Section, Select, Tabs, Toggle } from '../components/form';
import { Alert, Badge, Button, Input } from '../components/ui';
import { ApiError, api } from '../lib/api';

interface FeatureCode {
  id: string;
  action: string;
  code: string;
  enabled: boolean;
  label: string;
  group: string;
  takesArgument: boolean;
}

interface SpeedDial {
  id: string;
  code: string;
  number: string;
  name: string;
}

interface NumberRule {
  id: string;
  mode: string;
  direction: string;
  pattern: string;
  note: string | null;
}

export function FeatureCodes() {
  const [tab, setTab] = useState('codes');

  return (
    <div className="space-y-6">
      <PageHeader
        title="Feature codes"
        description="What users can dial from a handset. Defaults match what most PBXs use, so muscle memory carries over."
      />

      <Tabs
        tabs={[
          { id: 'codes', label: 'Feature codes' },
          { id: 'speed', label: 'Speed dial' },
          { id: 'numbers', label: 'Blocked numbers' },
          { id: 'paging', label: 'Paging and intercom' },
          { id: 'pins', label: 'PIN lists' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'codes' && <CodeList />}
      {tab === 'speed' && <SpeedDialList />}
      {tab === 'numbers' && <NumberRuleList />}
      {tab === 'paging' && <PagingGroupList />}
      {tab === 'pins' && <PinListView />}
    </div>
  );
}

function CodeList() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Record<string, string>>({});

  const { data: codes = [] } = useQuery({
    queryKey: ['feature-codes'],
    queryFn: () => api.get<FeatureCode[]>('/api/v1/feature-codes'),
  });

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<FeatureCode> }) =>
      api.patch(`/api/v1/feature-codes/${id}`, patch),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['feature-codes'] });
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'could not save'),
  });

  const columns: Array<Column<FeatureCode>> = [
    {
      key: 'label',
      header: 'Feature',
      sort: (a, b) => a.label.localeCompare(b.label),
      render: (c) => <span className="font-medium">{c.label}</span>,
    },
    {
      key: 'group',
      header: 'Group',
      sort: (a, b) => a.group.localeCompare(b.group),
      render: (c) => <Badge>{c.group}</Badge>,
    },
    {
      key: 'code',
      header: 'Code',
      sort: (a, b) => a.code.localeCompare(b.code),
      render: (c) => (
        <div className="flex items-center gap-2">
          <Input
            className="h-8 w-28 font-mono"
            value={editing[c.id] ?? c.code}
            onChange={(e) => setEditing((cur) => ({ ...cur, [c.id]: e.target.value }))}
            onBlur={() => {
              const next = editing[c.id];
              if (next && next !== c.code) update.mutate({ id: c.id, patch: { code: next } });
            }}
            aria-label={`Code for ${c.label}`}
          />
          {c.takesArgument && (
            <span className="whitespace-nowrap text-xs text-muted-foreground">+ number</span>
          )}
        </div>
      ),
    },
    {
      key: 'enabled',
      header: 'Enabled',
      align: 'right',
      render: (c) => (
        <div className="flex justify-end">
          <Toggle
            checked={c.enabled}
            onChange={(enabled) => update.mutate({ id: c.id, patch: { enabled } })}
            label={`Enable ${c.label}`}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {error && <Alert>{error}</Alert>}

      <Alert tone="warning">
        Codes are matched longest-first, so <code>*3</code> and <code>*31</code> can coexist. Avoid
        a code that prefixes an extension number — the engine would claim the call before it reached
        the phone.
      </Alert>

      <DataTable
        rows={codes}
        columns={columns}
        rowKey={(c) => c.id}
        search={(c) => `${c.label} ${c.code} ${c.group}`}
        searchPlaceholder="Search features"
        pageSize={50}
        empty="No feature codes."
      />
    </div>
  );
}

function SpeedDialList() {
  const queryClient = useQueryClient();
  // null = closed, '' = creating, an id = editing that entry.
  const [editing, setEditing] = useState<string | null>(null);
  const creating = editing === '';
  const [code, setCode] = useState('');
  const [number, setNumber] = useState('');
  const [name, setName] = useState('');

  const { data: dials = [] } = useQuery({
    queryKey: ['speed-dials'],
    queryFn: () => api.get<SpeedDial[]>('/api/v1/speed-dials'),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['speed-dials'] });

  const create = useMutation({
    mutationFn: () => {
      const body = { code, number, name };
      return creating
        ? api.post('/api/v1/speed-dials', body)
        : api.put(`/api/v1/speed-dials/${editing}`, body);
    },
    onSuccess: () => {
      setEditing(null);
      setCode('');
      setNumber('');
      setName('');
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/v1/speed-dials/${id}`),
    onSuccess: invalidate,
  });

  const conflict = create.error instanceof ApiError && create.error.isConflict;

  /** Load one entry into the form. The list carries everything the form needs. */
  const startEdit = (entry: SpeedDial) => {
    setCode(entry.code);
    setNumber(entry.number);
    setName(entry.name);
    setEditing(entry.id);
  };

  const columns: Array<Column<SpeedDial>> = [
    {
      key: 'code',
      header: 'Dial',
      sort: (a, b) => a.code.localeCompare(b.code),
      render: (d) => <code>*89{d.code}</code>,
    },
    {
      key: 'name',
      header: 'Name',
      sort: (a, b) => a.name.localeCompare(b.name),
      render: (d) => <span className="font-medium">{d.name}</span>,
    },
    {
      key: 'number',
      header: 'Number',
      render: (d) => <span className="font-mono">{d.number}</span>,
    },
  ];

  return (
    <div className="space-y-4">
      {editing !== null && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <Section title={creating ? 'New speed dial' : 'Edit speed dial'}>
            <div className="grid gap-4 sm:grid-cols-3">
              <Row
                label="Short code"
                required
                hint="Dialled after the speed-dial prefix."
                error={conflict ? 'That code is already used' : undefined}
              >
                <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="1" />
              </Row>
              <Row label="Name" required>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Head office"
                />
              </Row>
              <Row label="Number" required>
                <Input
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                  placeholder="+441234567890"
                />
              </Row>
            </div>

            {create.error && !conflict && (
              <div className="mt-4">
                <Alert>{(create.error as Error).message}</Alert>
              </div>
            )}

            <div className="mt-4 flex gap-2">
              <Button type="submit" disabled={create.isPending || !code || !number}>
                {create.isPending ? 'Saving…' : creating ? 'Create' : 'Save'}
              </Button>
              <Button type="button" variant="secondary" onClick={() => setEditing(null)}>
                Cancel
              </Button>
            </div>
          </Section>
        </form>
      )}

      <DataTable
        rows={dials}
        columns={columns}
        rowKey={(d) => d.id}
        search={(d) => `${d.code} ${d.name} ${d.number}`}
        searchPlaceholder="Search speed dials"
        toolbar={
          <Button onClick={() => setEditing('')}>
            <Plus size={14} />
            New speed dial
          </Button>
        }
        onEdit={startEdit}
        onDelete={(rows) => rows.forEach((r) => remove.mutate(r.id))}
        empty="No speed dials yet."
      />
    </div>
  );
}

function NumberRuleList() {
  const queryClient = useQueryClient();
  // null = closed, '' = creating, an id = editing that row.
  const [editing, setEditing] = useState<string | null>(null);
  const creating = editing === '';
  const [pattern, setPattern] = useState('');
  const [mode, setMode] = useState('block');
  const [direction, setDirection] = useState('both');
  const [note, setNote] = useState('');

  const { data: rules = [] } = useQuery({
    queryKey: ['number-rules'],
    queryFn: () => api.get<NumberRule[]>('/api/v1/number-rules'),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['number-rules'] });

  const create = useMutation({
    mutationFn: () =>
      (() => {
        const body = { pattern, mode, direction, ...(note ? { note } : {}) };
        return creating
          ? api.post('/api/v1/number-rules', body)
          : api.put(`/api/v1/number-rules/${editing}`, body);
      })(),
    onSuccess: () => {
      setEditing(null);
      setPattern('');
      setNote('');
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/v1/number-rules/${id}`),
    onSuccess: invalidate,
  });

  const startEdit = (rule: NumberRule) => {
    setPattern(rule.pattern);
    setMode(rule.mode);
    setDirection(rule.direction);
    setNote(rule.note ?? '');
    setEditing(rule.id);
  };

  const columns: Array<Column<NumberRule>> = [
    {
      key: 'pattern',
      header: 'Number or pattern',
      sort: (a, b) => a.pattern.localeCompare(b.pattern),
      render: (r) => <code>{r.pattern}</code>,
    },
    {
      key: 'mode',
      header: 'Action',
      render: (r) => (
        <Badge tone={r.mode === 'block' ? 'destructive' : 'success'}>
          {r.mode === 'block' ? 'Blocked' : 'Allowed'}
        </Badge>
      ),
    },
    { key: 'direction', header: 'Applies to', render: (r) => r.direction },
    { key: 'note', header: 'Note', render: (r) => r.note ?? '—' },
  ];

  return (
    <div className="space-y-4">
      <Alert tone="warning">
        Allow rules win over block rules, so a specific number can be excepted from a broad pattern.
        Patterns accept Asterisk wildcards: <code>X</code> is any digit, <code>.</code> is one or
        more.
      </Alert>

      {editing !== null && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <Section title={creating ? 'New rule' : 'Edit rule'}>
            <div className="grid gap-4 sm:grid-cols-4">
              <Row label="Number or pattern" required>
                <Input
                  value={pattern}
                  onChange={(e) => setPattern(e.target.value)}
                  placeholder="+44190XXXXXXX"
                />
              </Row>
              <Row label="Action" required>
                <Select value={mode} onChange={setMode}>
                  <option value="block">Block</option>
                  <option value="allow">Allow</option>
                </Select>
              </Row>
              <Row label="Applies to" required>
                <Select value={direction} onChange={setDirection}>
                  <option value="both">Inbound and outbound</option>
                  <option value="inbound">Inbound only</option>
                  <option value="outbound">Outbound only</option>
                </Select>
              </Row>
              <Row label="Note">
                <Input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Nuisance caller"
                />
              </Row>
            </div>

            {create.error && (
              <div className="mt-4">
                <Alert>{(create.error as Error).message}</Alert>
              </div>
            )}

            <div className="mt-4 flex gap-2">
              <Button type="submit" disabled={create.isPending || !pattern}>
                {create.isPending ? 'Saving…' : creating ? 'Create' : 'Save'}
              </Button>
              <Button type="button" variant="secondary" onClick={() => setEditing(null)}>
                Cancel
              </Button>
            </div>
          </Section>
        </form>
      )}

      <DataTable
        rows={rules}
        columns={columns}
        rowKey={(r) => r.id}
        search={(r) => `${r.pattern} ${r.note ?? ''}`}
        searchPlaceholder="Search rules"
        toolbar={
          <Button onClick={() => setEditing('')}>
            <Plus size={14} />
            New rule
          </Button>
        }
        onEdit={startEdit}
        onDelete={(rows) => rows.forEach((r) => remove.mutate(r.id))}
        empty="No rules. Every number is allowed."
      />
    </div>
  );
}

interface PagingGroup {
  id: string;
  name: string;
  number: string;
  duplex: boolean;
  memberExtensionIds: string[];
}

function PagingGroupList() {
  const queryClient = useQueryClient();
  // null = closed, '' = creating, an id = editing that group.
  const [editing, setEditing] = useState<string | null>(null);
  const creating = editing === '';
  const [form, setForm] = useState({
    name: '',
    number: '',
    duplex: false,
    members: [] as string[],
  });

  const { data: groups = [] } = useQuery({
    queryKey: ['paging-groups'],
    queryFn: () => api.get<PagingGroup[]>('/api/v1/paging-groups'),
  });

  const { data: extensions = [] } = useQuery({
    queryKey: ['extensions'],
    queryFn: () =>
      api.get<Array<{ id: string; number: string; name: string }>>('/api/v1/extensions'),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['paging-groups'] });

  const create = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name,
        number: form.number,
        duplex: form.duplex,
        memberExtensionIds: form.members,
      };
      return creating
        ? api.post('/api/v1/paging-groups', body)
        : api.put(`/api/v1/paging-groups/${editing}`, body);
    },
    onSuccess: () => {
      setEditing(null);
      setForm({ name: '', number: '', duplex: false, members: [] });
      invalidate();
    },
  });

  /** Load one group into the form. The list already carries everything the form needs. */
  const startEdit = (group: PagingGroup) => {
    setForm({
      name: group.name,
      number: group.number,
      duplex: group.duplex,
      members: group.memberExtensionIds ?? [],
    });
    setEditing(group.id);
  };

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/v1/paging-groups/${id}`),
    onSuccess: invalidate,
  });

  const columns: Array<Column<PagingGroup>> = [
    {
      key: 'name',
      header: 'Name',
      sort: (a, b) => a.name.localeCompare(b.name),
      render: (g) => <span className="font-medium">{g.name}</span>,
    },
    { key: 'number', header: 'Dial', render: (g) => <code>{g.number}</code> },
    {
      key: 'type',
      header: 'Type',
      render: (g) =>
        g.duplex ? <Badge tone="success">Two-way intercom</Badge> : <Badge>One-way paging</Badge>,
    },
    {
      key: 'members',
      header: 'Phones',
      sort: (a, b) => a.memberExtensionIds.length - b.memberExtensionIds.length,
      render: (g) => <span className="tabular-nums">{g.memberExtensionIds.length}</span>,
    },
  ];

  return (
    <div className="space-y-4">
      <Alert tone="warning">
        Paged phones answer themselves. That is requested with SIP headers, and a handset that does
        not support auto-answer will simply ring instead.
      </Alert>

      {creating && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <Section title={creating ? 'New paging group' : 'Edit paging group'}>
            <div className="grid gap-4 sm:grid-cols-3">
              <Row label="Name" required>
                <Input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="All phones"
                />
              </Row>
              <Row label="Dial" required>
                <Input
                  value={form.number}
                  onChange={(e) => setForm((f) => ({ ...f, number: e.target.value }))}
                  placeholder="500"
                />
              </Row>
              <Row label="Type" required hint="Two-way lets the other end answer back.">
                <Select
                  value={form.duplex ? 'duplex' : 'paging'}
                  onChange={(v) => setForm((f) => ({ ...f, duplex: v === 'duplex' }))}
                >
                  <option value="paging">One-way paging</option>
                  <option value="duplex">Two-way intercom</option>
                </Select>
              </Row>
            </div>

            <div className="mt-4">
              <Row label="Phones" hint="Hold ctrl or cmd to choose several.">
                <select
                  multiple
                  size={Math.min(8, Math.max(3, extensions.length))}
                  value={form.members}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      members: Array.from(e.target.selectedOptions, (o) => o.value),
                    }))
                  }
                  // Multi-select stays native: Radix has no multiple variant, and a listbox of
                  // checkboxes would be a worse fit for picking twenty phones at once.
                  className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs"
                >
                  {extensions.map((ext) => (
                    <option key={ext.id} value={ext.id}>
                      {ext.number} — {ext.name}
                    </option>
                  ))}
                </select>
              </Row>
            </div>

            {create.error && (
              <div className="mt-4">
                <Alert>{(create.error as Error).message}</Alert>
              </div>
            )}

            <div className="mt-4 flex gap-2">
              <Button type="submit" disabled={create.isPending || !form.name || !form.number}>
                {create.isPending ? 'Saving…' : creating ? 'Create' : 'Save'}
              </Button>
              <Button type="button" variant="secondary" onClick={() => setEditing(null)}>
                Cancel
              </Button>
            </div>
          </Section>
        </form>
      )}

      <DataTable
        rows={groups}
        columns={columns}
        rowKey={(g) => g.id}
        search={(g) => `${g.name} ${g.number}`}
        searchPlaceholder="Search groups"
        toolbar={
          <Button
            onClick={() => {
              setForm({ name: '', number: '', duplex: false, members: [] });
              setEditing('');
            }}
          >
            <Plus size={14} />
            New paging group
          </Button>
        }
        onEdit={startEdit}
        onDelete={(rows) => rows.forEach((r) => remove.mutate(r.id))}
        empty="No paging groups."
      />
    </div>
  );
}

interface PinList {
  id: string;
  name: string;
  recordInCdr: boolean;
  codes: Array<{ id: string; code: string; label: string | null }>;
}

function PinListView() {
  const queryClient = useQueryClient();
  // null = closed, '' = creating, an id = editing that row.
  const [editing, setEditing] = useState<string | null>(null);
  const creating = editing === '';
  const [name, setName] = useState('');
  const [recordInCdr, setRecordInCdr] = useState(true);
  const [codes, setCodes] = useState('');

  const { data: lists = [] } = useQuery({
    queryKey: ['pin-lists'],
    queryFn: () => api.get<PinList[]>('/api/v1/pin-lists'),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['pin-lists'] });

  const create = useMutation({
    mutationFn: () => {
      const body = {
        name,
        recordInCdr,
        // One per line as "code, label" — pasting a list from a spreadsheet is how these arrive.
        codes: codes
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean)
          .map((line) => {
            const [code, ...rest] = line.split(/[,\t]/);
            return {
              code: (code ?? '').trim(),
              ...(rest.length > 0 ? { label: rest.join(',').trim() } : {}),
            };
          }),
      };

      return creating
        ? api.post('/api/v1/pin-lists', body)
        : api.put(`/api/v1/pin-lists/${editing}`, body);
    },
    onSuccess: () => {
      setEditing(null);
      setName('');
      setCodes('');
      invalidate();
    },
  });

  /**
   * Load one list into the form.
   *
   * Codes come back as rows and go out as pasted text, so they are rendered back into the same
   * "code, label" lines an admin pasted in. Round-tripping matters here: editing the name of a
   * list should not silently reformat two hundred PINs.
   */
  const startEdit = (list: PinList) => {
    setName(list.name);
    setRecordInCdr(list.recordInCdr);
    setCodes(
      (list.codes ?? []).map((c) => (c.label ? `${c.code}, ${c.label}` : c.code)).join('\n'),
    );
    setEditing(list.id);
  };

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/v1/pin-lists/${id}`),
    onSuccess: invalidate,
  });

  const columns: Array<Column<PinList>> = [
    {
      key: 'name',
      header: 'Name',
      sort: (a, b) => a.name.localeCompare(b.name),
      render: (l) => <span className="font-medium">{l.name}</span>,
    },
    {
      key: 'count',
      header: 'Codes',
      sort: (a, b) => a.codes.length - b.codes.length,
      render: (l) => <span className="tabular-nums">{l.codes.length}</span>,
    },
    {
      key: 'cdr',
      header: 'Recorded on the call',
      render: (l) => (l.recordInCdr ? <Badge tone="success">Yes</Badge> : <Badge>No</Badge>),
    },
    {
      key: 'labels',
      header: 'Used by',
      render: (l) => {
        const labels = l.codes.map((c) => c.label).filter(Boolean);
        return labels.length > 0 ? labels.slice(0, 4).join(', ') : '—';
      },
    },
  ];

  return (
    <div className="space-y-4">
      <Alert tone="warning">
        Attach a list to an outbound route and the caller must enter a code before the call is
        placed. The code is asked for before the trunk is engaged, so a wrong one costs nothing.
      </Alert>

      {editing !== null && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <Section title={creating ? 'New PIN list' : 'Edit PIN list'}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Row label="Name" required>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="International"
                />
              </Row>
              <Row
                label="Record on the call"
                hint="Writes which code was used to the call record, for departmental billing."
              >
                <Select
                  value={recordInCdr ? 'yes' : 'no'}
                  onChange={(v) => setRecordInCdr(v === 'yes')}
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </Select>
              </Row>
            </div>

            <div className="mt-4">
              <Row
                label="Codes"
                required
                hint="One per line, as code or code, label. 3 to 16 digits."
              >
                <textarea
                  value={codes}
                  onChange={(e) => setCodes(e.target.value)}
                  rows={6}
                  placeholder={'1234, Sales\n5678, Support'}
                  className="w-full rounded-[--radius] border border-input bg-background px-3 py-2 font-mono text-sm"
                />
              </Row>
            </div>

            {create.error && (
              <div className="mt-4">
                <Alert>{(create.error as Error).message}</Alert>
              </div>
            )}

            <div className="mt-4 flex gap-2">
              <Button type="submit" disabled={create.isPending || !name || !codes.trim()}>
                {create.isPending ? 'Saving…' : creating ? 'Create' : 'Save'}
              </Button>
              <Button type="button" variant="secondary" onClick={() => setEditing(null)}>
                Cancel
              </Button>
            </div>
          </Section>
        </form>
      )}

      <DataTable
        rows={lists}
        columns={columns}
        rowKey={(l) => l.id}
        search={(l) => l.name}
        searchPlaceholder="Search lists"
        toolbar={
          <Button onClick={() => setEditing('')}>
            <Plus size={14} />
            New PIN list
          </Button>
        }
        onEdit={startEdit}
        onDelete={(rows) => rows.forEach((r) => remove.mutate(r.id))}
        deleteLabel={() => 'Delete this list? Routes using it will stop asking for a PIN.'}
        empty="No PIN lists."
      />
    </div>
  );
}
